import React, { useEffect, useRef, useState } from "react"

/**
 * CinematicHero — couches média d'un héros vidéo, en styles inline.
 *
 * Reprend à l'identique la recette validée de `typesense/CinemaHeroMedia` :
 *   – le poster est le LCP (eager, fetchpriority high, decoding sync) ;
 *   – la vidéo est montée APRÈS `load`, en preload="none", puis fondue ;
 *   – `prefers-reduced-motion` ⇒ photo seule, aucun mouvement.
 * `posterMobile` (optionnel) : poster distinct sous `mobileQuery`, qui règle
 * aussi le choix de `videoMobile` (défaut 820 px, inchangé pour l'existant).
 *
 * Deux écarts assumés avec l'original : styles inline plutôt que Tailwind
 * (les classes arbitraires ne sont pas toujours générées dans ce repo), et
 * une source mobile distincte quand elle existe.
 *
 * ⚠️ Les deux couches média sont à opacité 1 — l'assombrissement vient du
 * SEUL scrim. Superposer deux médias translucides produit un fantôme.
 */
const CinematicHero = ({
  poster,
  video,
  videoMobile,
  posterMobile,
  mobileQuery = "(max-width: 820px)",
  scrim = "linear-gradient(180deg,rgba(0,0,0,0.52) 0%,rgba(0,0,0,0.20) 42%,rgba(0,0,0,0.74) 100%)",
  kenBurns = true,
}) => {
  const videoRef = useRef(null)
  const [videoOn, setVideoOn] = useState(false)

  useEffect(() => {
    if (typeof window === "undefined") return undefined
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined

    const mountVideo = () => {
      const v = videoRef.current
      if (!v || v.src) return
      const narrow = window.matchMedia(mobileQuery).matches
      v.src = narrow && videoMobile ? videoMobile : video
      v.addEventListener("playing", () => setVideoOn(true), { once: true })
      const p = v.play()
      if (p && typeof p.then === "function") p.then(() => setVideoOn(true)).catch(() => {})
    }

    if (document.readyState === "complete") {
      const t = setTimeout(mountVideo, 400)
      return () => clearTimeout(t)
    }
    window.addEventListener("load", mountVideo, { once: true })
    return () => window.removeEventListener("load", mountVideo)
  }, [video, videoMobile, mobileQuery])

  return (
    <div style={{ position: "absolute", inset: 0, pointerEvents: "none" }} aria-hidden="true">
      <picture>
        {posterMobile && <source media={mobileQuery} srcSet={posterMobile} />}
        <img
          src={poster}
          alt=""
          loading="eager"
          fetchpriority="high"
          decoding="sync"
          className={kenBurns ? "ch-poster" : undefined}
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
        />
      </picture>
      {video && (
        <video
          ref={videoRef}
          muted
          loop
          playsInline
          preload="none"
          style={{
            position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover",
            opacity: videoOn ? 1 : 0, transition: "opacity 1600ms ease-out",
          }}
        />
      )}
      <div style={{ position: "absolute", inset: 0, background: scrim }} />
      <style>{`
        .ch-poster { transform: scale(1.08); animation: chKenburns 26s cubic-bezier(0.3,0.7,0.25,1) forwards; }
        @keyframes chKenburns { to { transform: scale(1); } }
        @media (prefers-reduced-motion: reduce) {
          .ch-poster { animation: none !important; transform: none !important; }
        }
      `}</style>
    </div>
  )
}

export default CinematicHero
